import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ForestProfileContext = createContext(null);

export const ForestProfileProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [forestProfile, setForestProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load forest profile from localStorage on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      const savedProfile = localStorage.getItem(`forest_profile_${user.email}`);
      if (savedProfile) {
        try {
          setForestProfile(JSON.parse(savedProfile));
        } catch (e) {
          console.error('Error parsing forest profile:', e);
        }
      }
    } else {
      setForestProfile(null);
    }
    setLoading(false);
  }, [isAuthenticated, user]);

  // Save forest profile
  const saveForestProfile = (profile) => {
    if (!user) return;
    
    const profileWithTimestamp = {
      ...profile,
      updatedAt: new Date().toISOString()
    };
    
    setForestProfile(profileWithTimestamp);
    localStorage.setItem(`forest_profile_${user.email}`, JSON.stringify(profileWithTimestamp));
    return profileWithTimestamp;
  };

  // Update specific fields
  const updateForestProfile = (updates) => {
    if (!forestProfile) return null;
    return saveForestProfile({ ...forestProfile, ...updates });
  };

  // Delete forest profile
  const deleteForestProfile = () => {
    if (!user) return;
    setForestProfile(null);
    localStorage.removeItem(`forest_profile_${user.email}`);
  };

  // Get prefill data for calculators
  const getPrefillData = () => {
    if (!forestProfile) return null;
    
    return {
      area: forestProfile.area || 0,
      volume: forestProfile.volume || 0,
      volumePerHa: forestProfile.area > 0 ? Math.round(forestProfile.volume / forestProfile.area) : 0,
      species: forestProfile.species || 'gran',
      region: forestProfile.region || 'svealand',
      growthRate: forestProfile.growthRate || 5,
      name: forestProfile.name || ''
    };
  };

  // Check if profile exists
  const hasProfile = !!forestProfile && !!forestProfile.name;

  return (
    <ForestProfileContext.Provider value={{
      forestProfile,
      loading,
      saveForestProfile,
      updateForestProfile,
      deleteForestProfile,
      getPrefillData,
      hasProfile
    }}>
      {children}
    </ForestProfileContext.Provider>
  );
};

export const useForestProfile = () => {
  const context = useContext(ForestProfileContext);
  if (!context) {
    throw new Error('useForestProfile must be used within a ForestProfileProvider');
  }
  return context;
};

export default ForestProfileContext;
